// Org-tier DIDs list. One row per phone number in the org's Commio inventory.
// Phase 1: read-only. Row click links to /dids/:e164 (currently the
// Placeholder route in App.jsx — the detail page swaps in later).
//
// Portal parity notes:
//   - Portal renders a live serviceStatus (online/down/no-account/…) derived
//     from cross-referencing agent registration probes. The org-tier API
//     doesn't surface that field yet — this pass only shows the DID's
//     configured status.
//   - Portal shows a CNAM/E911/SMS feature triplet. This pass renders CNAM
//     only (a single string column); E911/SMS are per-DID booleans that
//     aren't in the current /dids response envelope.

import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { api, IMPORT_TIMEOUT_MS } from '../lib/api.js';
import { useAuth } from '../hooks/useAuth.js';
import { usePbxNames } from '../hooks/usePbxNames.js';
import { canOperate } from '../lib/roles.js';
import ActionMenu from '../components/shared/ActionMenu.jsx';
import ConfirmDeleteDialog from '../components/shared/ConfirmDeleteDialog.jsx';
import Pagination, { usePagination } from '../components/shared/Pagination.jsx';
import LoadingSpinner from '../components/shared/LoadingSpinner.jsx';
import StatusBadge from '../components/shared/StatusBadge.jsx';
import ImportMenu from '../components/shared/ImportMenu.jsx';
import ListToolbar from '../components/shared/ListToolbar.jsx';
import SortableHeader, { useSort } from '../components/shared/SortableHeader.jsx';
import { sharedList } from '../lib/liveQuery.js';

// "In inventory" rather than "Unassigned". A number we own and have not sold is
// stock, not a gap — and it is the tab an operator opens to find one to sell, which
// is a positive act, not a cleanup.
const DID_STATUS_FILTERS = [
  { key: 'all',       label: 'All' },
  { key: 'assigned',  label: 'Assigned' },
  { key: 'inventory', label: 'In inventory' },
  { key: 'cnam',      label: 'CNAM' },
  { key: 'e911',      label: 'E911' },
  { key: 'sms',       label: 'SMS' },
];

// Cheap NANP formatter. Non-NANP falls back to the raw e164 string.
function fmtDid(e164) {
  if (!e164) return '';
  const s = String(e164).replace(/[^0-9]/g, '');
  if (s.length === 11 && s[0] === '1') return `+1 (${s.slice(1, 4)}) ${s.slice(4, 7)}-${s.slice(7)}`;
  if (s.length === 10) return `(${s.slice(0, 3)}) ${s.slice(3, 6)}-${s.slice(6)}`;
  return String(e164);
}
const stripPlus = (e) => String(e).replace(/^\+/, '');

// Compact feature badges — CNAM, E911, SMS. Reads .features (JSON blob
// populated on Commio import) as the primary source; falls back to the
// top-level cnam column (display name) when features_json isn't there
// yet, so pre-migration rows still show a CNAM badge.
function FeatureBadges({ row }) {
  const features = row.features || {};
  const cnam = Boolean(features.cnam) || Boolean(row.cnam);
  const e911 = Boolean(features.e911);
  const sms  = Boolean(features.sms);

  const badges = [];
  if (cnam) badges.push({
    key: 'cnam',
    label: 'CNAM',
    cls: 'bg-blue-100 text-blue-700',
    title: row.cnam || (features.raw?.caller_id_name) || 'CNAM configured',
  });
  if (e911) badges.push({
    key: 'e911',
    label: 'E911',
    cls: 'bg-green-100 text-green-700',
    title: 'E911 location assigned',
  });
  if (sms) badges.push({
    key: 'sms',
    label: 'SMS',
    cls: 'bg-purple-100 text-purple-700',
    title: 'Messaging enabled',
  });

  if (badges.length === 0) return <span className="text-gray-300">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {badges.map((b) => (
        <span
          key={b.key}
          title={b.title}
          className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${b.cls}`}
        >
          {b.label}
        </span>
      ))}
    </div>
  );
}

function didStatusVariant(status) {
  switch (status) {
    case 'active':       return 'active';
    case 'assigned':     return 'active';
    // Its own colour, not another grey. Grey in this palette means inactive,
    // unknown or disconnected — something wrong or unanswered — and a stocked
    // number is none of those.
    case 'in inventory': return 'inventory';
    case 'pending':      return 'pending';
    case 'released':     return 'critical';
    default:             return 'unknown';
  }
}

export default function DidsList() {
  const { pbxName: lookupPbxName } = usePbxNames(); // agent_id → name (never show the UUID)
  const { role } = useAuth();
  const canImport = canOperate(role);
  const queryClient = useQueryClient();
  const [importToast, setImportToast] = useState(null);
  const navigate = useNavigate();
  // Row kebab: unassign a DID from its subscriber (type-delete confirm).
  const [unassigning, setUnassigning] = useState(null);
  const unassignDid = useMutation({
    mutationFn: (e164) => api.delete(`/dids/${encodeURIComponent(stripPlus(e164))}/subscriber`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dids'] });
      setUnassigning(null);
    },
  });

  const sort = useSort({ col: 'e164', dir: 'asc' });
  const { data, isLoading, error } = useQuery({
    queryKey: ['dids', sort.col, sort.dir],
    queryFn: () => api.get('/dids', { sort: sort.col, dir: sort.dir }),
    ...sharedList(),
  });

  const importMutation = useMutation({
    mutationFn: () => api.post('/dids/import', undefined, undefined, { timeoutMs: IMPORT_TIMEOUT_MS }),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['dids'] });
      const n = res?.dids_upserted ?? res?.imported ?? 0;
      setImportToast({
        ok: true,
        message: `Imported ${n} DID${n === 1 ? '' : 's'} from Commio`
          + (res?.errors?.length ? ` (${res.errors.length} error${res.errors.length === 1 ? '' : 's'})` : ''),
      });
      setTimeout(() => setImportToast(null), 5000);
    },
    onError: (err) => {
      setImportToast({
        ok: false,
        message: err?.body?.error || err?.message || 'Import failed',
      });
      setTimeout(() => setImportToast(null), 5000);
    },
  });

  const allRows = useMemo(() => {
    if (Array.isArray(data))       return data;
    if (Array.isArray(data?.rows)) return data.rows;
    if (Array.isArray(data?.dids)) return data.dids;
    return [];
  }, [data]);

  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const isAssigned = (r) => (r.subscriber?.id || r.subscriber_id || r.subscriberId) != null;
  const hasCnam = (r) => Boolean(r.features?.cnam) || Boolean(r.cnam);
  const hasE911 = (r) => Boolean(r.features?.e911);
  const hasSms  = (r) => Boolean(r.features?.sms);
  const statusCounts = useMemo(() => ({
    all:       allRows.length,
    assigned:  allRows.filter(isAssigned).length,
    inventory: allRows.filter((r) => !isAssigned(r)).length,
    cnam:      allRows.filter(hasCnam).length,
    e911:      allRows.filter(hasE911).length,
    sms:       allRows.filter(hasSms).length,
  }), [allRows]);
  const rows = useMemo(() => {
    let out = allRows;
    if (statusFilter === 'assigned')  out = out.filter(isAssigned);
    if (statusFilter === 'inventory') out = out.filter((r) => !isAssigned(r));
    if (statusFilter === 'cnam')       out = out.filter(hasCnam);
    if (statusFilter === 'e911')       out = out.filter(hasE911);
    if (statusFilter === 'sms')        out = out.filter(hasSms);
    const s = search.trim().toLowerCase();
    if (s) {
      out = out.filter((r) => {
        const e164 = r.e164 || r.did || r.number || '';
        const cnam = r.cnam || '';
        const subName = r.subscriber?.display_name || r.subscriber?.name || r.subscriberName || r.subscriber_name || '';
        return (
          String(e164).replace(/[^0-9]/g, '').includes(s.replace(/[^0-9]/g, '')) ||
          String(cnam).toLowerCase().includes(s) ||
          String(subName).toLowerCase().includes(s)
        );
      });
    }
    return out;
  }, [allRows, statusFilter, search]);

  const { paged: pagedRows, pagination } = usePagination(rows);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">DIDs</h1>
        <div className="flex items-center gap-3">
          {allRows.length > 0 && (
            <span className="text-sm text-gray-500">
              {rows.length === allRows.length
                ? `${allRows.length} number${allRows.length === 1 ? '' : 's'}`
                : `${rows.length} of ${allRows.length}`}
            </span>
          )}
          {canImport && (
            <ImportMenu
              pending={importMutation.isPending}
              items={[
                { label: 'Import from Commio', onClick: () => importMutation.mutate() },
              ]}
            />
          )}
        </div>
      </div>

      {importToast && (
        <div
          className={`rounded-md border px-4 py-2 text-sm ${
            importToast.ok
              ? 'border-green-200 bg-green-50 text-green-700'
              : 'border-red-200 bg-red-50 text-red-700'
          }`}
        >
          {importToast.message}
        </div>
      )}

      <ListToolbar
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Filter by number, CNAM, or subscriber…"
        filters={DID_STATUS_FILTERS}
        filterCounts={statusCounts}
        activeFilter={statusFilter}
        onFilterChange={setStatusFilter}
      />

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
          {error.message}
        </div>
      )}

      <div className="rounded-lg border border-gray-200 bg-white">
        {isLoading ? (
          <LoadingSpinner message="Loading DIDs..." />
        ) : rows.length === 0 ? (
          <div className="p-12 text-center">
            <p className="text-sm text-gray-500">No DIDs yet.</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs text-gray-500">
              <tr>
                <SortableHeader col="status"     sort={sort} label="Status" />
                <SortableHeader col="pbx"        sort={sort} label="PBX" />
                <SortableHeader col="e164"       sort={sort} label="DID" />
                <SortableHeader col="subscriber" sort={sort} label="Subscriber" />
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Features</th>
                {canImport && <th className="px-2 py-2" />}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {pagedRows.map((r) => {
                const e164 = r.e164 || r.did || r.number;
                const sub = r.subscriber || {};
                const subName = sub.display_name || sub.displayName || sub.name || r.subscriberName || r.subscriber_name;
                const subId = sub.id || sub.subscriber_id || r.subscriberId || r.subscriber_id;
                const pbxName = r.pbxName || r.pbx_name;
                // Assignment drives the badge: a number with no subscriber reads
                // "in inventory" regardless of its Commio status
                // (which is "active" for everything in the account).
                const status = subId ? (r.status || 'assigned') : 'in inventory';
                return (
                  <tr key={e164} className="hover:bg-gray-50">
                    <td className="px-4 py-2">
                      <StatusBadge status={didStatusVariant(status)} label={status} />
                    </td>
                    <td className="px-4 py-2 text-xs text-gray-600">
                      {pbxName
                        ? pbxName
                        : (r.pbx_agent_id || r.pbxAgentId)
                          ? <span title={r.pbx_agent_id || r.pbxAgentId}>{lookupPbxName(r.pbx_agent_id || r.pbxAgentId)}</span>
                          : <span className="text-gray-300">—</span>}
                    </td>
                    <td className="px-4 py-2 font-mono">
                      <Link
                        to={`/dids/${encodeURIComponent(stripPlus(e164))}`}
                        className="text-blue-600 hover:underline"
                      >
                        {fmtDid(e164)}
                      </Link>
                    </td>
                    <td className="px-4 py-2">
                      {subId ? (
                        <Link to={`/subscribers/${subId}`} className="text-blue-600 hover:underline">
                          {subName || subId}
                        </Link>
                      ) : (
                        // Says what it IS, and in the same words as the badge and
                        // the filter tab, so all three agree.
                        <span className="text-indigo-700">In inventory</span>
                      )}
                    </td>
                    <td className="px-4 py-2">
                      <FeatureBadges row={r} />
                    </td>
                    {canImport && (
                      <td className="px-2 py-2 text-right">
                        <ActionMenu
                          title={`Actions for ${fmtDid(e164)}`}
                          items={[
                            { label: 'View details', onClick: () => navigate(`/dids/${encodeURIComponent(stripPlus(e164))}`) },
                            ...(subId ? [
                              { label: 'View subscriber', onClick: () => navigate(`/subscribers/${subId}`) },
                              { divider: true },
                              { label: 'Unassign from subscriber', danger: true, onClick: () => { unassignDid.reset(); setUnassigning(r); } },
                            ] : []),
                          ]}
                        />
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        <Pagination {...pagination} />
      </div>

      {unassigning && (
        <ConfirmDeleteDialog
          destructive={false}
          title="Unassign DID"
          body={`Unassign ${fmtDid(unassigning.e164)} from ${unassigning.subscriberName || unassigning.subscriber_name || 'its subscriber'}? The number stays in inventory; only the subscriber link is removed.`}
          confirmLabel="Unassign"
          isPending={unassignDid.isPending}
          error={unassignDid.isError ? (unassignDid.error?.body?.error || unassignDid.error?.message) : null}
          onCancel={() => setUnassigning(null)}
          onConfirm={() => unassignDid.mutate(unassigning.e164)}
        />
      )}
    </div>
  );
}
